/**
 * Prebuilt workflow templates. Each template builds a complete graph in the
 * React Flow shape stored on the workflow; the create mutation accepts the
 * graph directly, so a templated workflow runs out of the box.
 *
 * The cross-publication template ships placeholder publication filters
 * (id 0 matches nothing) — the user replaces them with two real publications.
 */

const pos = (i, y = 140) => ({ x: 80 + i * 280, y });

const node = (id, type, params = {}, i = 0, y = 140) => ({ id, type, params, position: pos(i, y) });
const edge = (id, source, target) => ({ id, source, target });

export const WORKFLOW_TEMPLATES = [
  {
    id: 'grave-stats',
    name: 'Grave statistics',
    description: 'Graves per site — a grouped bar chart of the grave counts.',
    entity: 'grave',
    build() {
      return {
        nodes: [
          node('source', 'source', { entity: 'grave', joins: ['site'], conditions: [] }),
          node('group', 'group', { by: ['site_name'] }, 1),
          node(
            'chart',
            'chart',
            { title: 'Graves per site', chartType: 'bar', config: { x: 'site_name', y: 'count' } },
            2
          )
        ],
        edges: [edge('e-source-group', 'source', 'group'), edge('e-group-chart', 'group', 'chart')]
      };
    }
  },
  {
    id: 'orientation',
    name: 'Skeleton orientation',
    description: 'Spine orientation against the arrow, binned into a distribution chart.',
    entity: 'spine',
    build() {
      return {
        nodes: [
          node('source', 'source', { entity: 'spine', joins: ['site'], conditions: [] }),
          node('orientation', 'orientation', { mode: 'skeleton', bin: 45 }, 1),
          node('group', 'group', { by: ['orientation_bin'] }, 2),
          node(
            'chart',
            'chart',
            {
              title: 'Orientation distribution',
              chartType: 'bar',
              config: { x: 'orientation_bin', y: 'count' }
            },
            3
          )
        ],
        edges: [
          edge('e-source-orientation', 'source', 'orientation'),
          edge('e-orientation-group', 'orientation', 'group'),
          edge('e-group-chart', 'group', 'chart')
        ]
      };
    }
  },
  {
    id: 'shape',
    name: 'Shape space (EFD)',
    description: 'EFD features of skeletons projected into a 2D PCA scatter, colored by site.',
    entity: 'skeleton',
    build() {
      return {
        nodes: [
          node('source', 'source', { entity: 'skeleton', joins: ['site'], conditions: [] }),
          node('shape', 'shape', { featureSet: 'efd', method: 'pca', components: 2 }, 1),
          node(
            'chart',
            'chart',
            {
              title: 'Skeleton shape space',
              chartType: 'scatter',
              config: { x: 'pc1', y: 'pc2', color: 'site_name' }
            },
            2
          )
        ],
        edges: [edge('e-source-shape', 'source', 'shape'), edge('e-shape-chart', 'shape', 'chart')]
      };
    }
  },
  {
    id: 'compare',
    name: 'Cross-publication comparison',
    description:
      'Two publication-filtered branches tagged as distinct series in one multi-series chart. Set both publication filters to real publications.',
    entity: 'grave',
    build() {
      const publication = (id) => [
        { target: 'entity', field: 'publication_id', op: 'eq', values: [id] }
      ];
      return {
        nodes: [
          node(
            'source-a',
            'source',
            { entity: 'grave', joins: ['site'], conditions: publication(0) },
            0,
            60
          ),
          node('series-a', 'series', { value: 'Publication A' }, 1, 60),
          node(
            'source-b',
            'source',
            { entity: 'grave', joins: ['site'], conditions: publication(0) },
            0,
            220
          ),
          node('series-b', 'series', { value: 'Publication B' }, 1, 220),
          node('group', 'group', { by: ['site_name', 'series'] }, 2, 140),
          node(
            'chart',
            'chart',
            {
              title: 'Graves per site — publications',
              chartType: 'bar',
              config: { x: 'site_name', y: 'count', seriesField: 'series' }
            },
            3,
            140
          )
        ],
        edges: [
          edge('e-source-a-series-a', 'source-a', 'series-a'),
          edge('e-source-b-series-b', 'source-b', 'series-b'),
          edge('e-series-a-group', 'series-a', 'group'),
          edge('e-series-b-group', 'series-b', 'group'),
          edge('e-group-chart', 'group', 'chart')
        ]
      };
    }
  },
  {
    id: 'heatmaps',
    name: 'Artefact heatmaps',
    description: 'Grave outlines with artefact density, grouped by burial orientation.',
    entity: 'spine',
    build() {
      return {
        nodes: [
          node('source', 'source', { entity: 'spine', joins: ['site'], conditions: [] }),
          node('heatmap', 'heatmap', { grouping: 'orientation' }, 1)
        ],
        edges: [edge('e-source-heatmap', 'source', 'heatmap')]
      };
    }
  },
  {
    id: 'similarity',
    name: 'Ceramic similarity matrix',
    description: 'Stored BOVW similarities of ceramics as an N×N similarity matrix.',
    entity: 'ceramic',
    build() {
      return {
        nodes: [
          node('source', 'source', { entity: 'ceramic', joins: ['site'], conditions: [] }),
          node('similarities', 'similarities', {}, 1),
          node(
            'chart',
            'chart',
            {
              title: 'Similarity matrix',
              chartType: 'matrix',
              config: { x: 'identifier', y: 'similar_to', value: 'similarity' }
            },
            2
          )
        ],
        edges: [
          edge('e-source-similarities', 'source', 'similarities'),
          edge('e-similarities-chart', 'similarities', 'chart')
        ]
      };
    }
  }
];
